import type { AxiosResponse } from "axios"
import { clean_params } from "@/core/helpers/utils"

type S3AuditResult = "success" | "denied" | "failed"
type S3AuditEventSort = "event_time_desc" | "event_time_asc"

export interface S3AuditEventSearchParams {
  from?: string
  until?: string
  event_type?: string
  s3_action?: string
  bucket_id?: number
  bucket_name?: string
  object_key?: string
  object_key_prefix?: string
  actor_type?: string
  actor_id?: string
  credential_id?: string
  access_key_id?: string
  request_id?: string
  trace_id?: string
  result?: S3AuditResult
  http_response_code?: number
  sealed?: boolean
  limit?: number
  cursor?: string
  sort?: S3AuditEventSort
}

export interface S3AuditBatchSearchParams {
  stream_id?: string
  from?: string
  until?: string
  limit?: number
  cursor?: string
}

export interface S3AuditStreamChainVerificationRequest {
  from_sequence?: number
  to_sequence?: number | null
}

export interface S3AuditEvent {
  id: number
  event_id: string
  project_id?: number | null
  account_id?: number | null
  stream_type: string
  stream_id: string
  event_type: string
  s3_action?: string | null
  http_method?: string | null
  request_path?: string | null
  bucket_id?: string | null
  bucket_name?: string | null
  object_key?: string | null
  object_version_id?: string | null
  upload_id?: string | null
  actor_type?: string | null
  actor_id?: string | null
  credential_id?: string | null
  access_key_id?: string | null
  request_id: string
  trace_id?: string | null
  source_ip?: string | null
  user_agent?: string | null
  result: S3AuditResult
  http_response_code?: number | null
  error_code?: string | null
  bytes_in?: number | null
  bytes_out?: number | null
  etag?: string | null
  event_time: string
  ingested_at?: string | null
  extra?: Record<string, unknown> | null
  sealed_batch_id?: string | null
  sealed_at?: string | null
  canonical_event_hash?: string | null
  sealed: boolean
}

export interface S3AuditEventSearchResponse {
  events: S3AuditEvent[]
  next_cursor: string | null
}

export interface S3AuditBatch {
  id: number
  batch_id: string
  stream_type: string
  stream_id: string
  sequence_number: number
  sealed_by?: string | null
  event_count: number
  first_event_id?: string | null
  last_event_id?: string | null
  first_event_time?: string | null
  last_event_time?: string | null
  previous_batch_id?: string | null
  previous_chain_hash?: string | null
  events_hash: string
  manifest_hash: string
  chain_hash: string
  signature_algorithm: string
  signature_key_id: string
  signature: string
  events_artifact_uri: string
  manifest_artifact_uri: string
  created_at?: string | null
  sealed_at?: string | null
}

export interface S3AuditBatchSearchResponse {
  batches: S3AuditBatch[]
  next_cursor: string | null
}

export interface S3AuditBatchManifest {
  manifest_version: string
  batch_id: string
  stream_type: string
  stream_id: string
  sequence_number: number
  event_count: number
  first_event_id: string | null
  last_event_id: string | null
  first_event_time: string | null
  last_event_time: string | null
  previous_batch_id: string | null
  previous_chain_hash: string | null
  events_hash_algorithm: string
  events_hash: string
  manifest_hash_algorithm: string
  manifest_hash: string
  chain_hash_algorithm: string
  chain_hash: string
  signature_algorithm: string
  signature_key_id: string
  signature: string
  events_artifact_uri: string
  manifest_artifact_uri: string
  created_at: string
}

export interface S3AuditBatchVerificationResult {
  batch_id: string
  valid: boolean
  checks: Record<string, boolean>
  errors: string[]
}

export interface S3AuditStreamChainBatchResult {
  batch_id: string
  sequence_number: number
  valid: boolean
  checks: Record<string, boolean>
  errors: string[]
}

export interface S3AuditStreamChainVerificationResult {
  stream_id: string
  from_sequence: number
  to_sequence: number | null
  valid: boolean
  checks: Record<string, boolean>
  errors: string[]
  batches: S3AuditStreamChainBatchResult[]
}

/**
 * @description service to call HTTP request via Axios
 */
class AuditLogService {
    /**
     * @description Service base URL (without trailing `/`)
     */
    public static base_url: string = import.meta.env.VITE_APP_AUDIT_LOG_SERVICE_URL

    public static axios: any

    public static init (axiosInstance: any) {
      AuditLogService.axios = axiosInstance
    }

    /**
     * @description Search S3 audit events. S3_DEV callers are scoped to their own team.
     */
    public static search_events(params: S3AuditEventSearchParams = {}): Promise<AxiosResponse<S3AuditEventSearchResponse>> {
      return AuditLogService.axios.get(`${AuditLogService.base_url}/s3-audit/events`, {
        params: clean_params({
          ...params,
          sealed: params.sealed === undefined ? undefined : Number(params.sealed)
        })
      })
    }

    /**
     * @description Get one S3 audit event by public event ID.
     */
    public static get_event(event_id: string): Promise<AxiosResponse<S3AuditEvent>> {
      return AuditLogService.axios.get(`${AuditLogService.base_url}/s3-audit/events/${event_id}`)
    }

    /**
     * @description List sealed S3 audit batch metadata. S3_DEV callers are scoped to their own team.
     */
    public static list_batches(params: S3AuditBatchSearchParams = {}): Promise<AxiosResponse<S3AuditBatchSearchResponse>> {
      return AuditLogService.axios.get(`${AuditLogService.base_url}/s3-audit/batches`, {
        params: clean_params(params)
      })
    }

    /**
     * @description Get one sealed S3 audit batch metadata record.
     */
    public static get_batch(batch_id: string): Promise<AxiosResponse<S3AuditBatch>> {
      return AuditLogService.axios.get(`${AuditLogService.base_url}/s3-audit/batches/${batch_id}`)
    }

    /**
     * @description Get one sealed S3 audit batch manifest artifact.
     */
    public static get_batch_manifest(batch_id: string): Promise<AxiosResponse<S3AuditBatchManifest>> {
      return AuditLogService.axios.get(`${AuditLogService.base_url}/s3-audit/batches/${batch_id}/manifest`)
    }

    /**
     * @description Get one sealed S3 audit batch events artifact as JSON Lines.
     */
    public static get_batch_events(batch_id: string): Promise<AxiosResponse<string>> {
      return AuditLogService.axios.get(`${AuditLogService.base_url}/s3-audit/batches/${batch_id}/events`, {
        responseType: "text"
      })
    }

    /**
     * @description Verify one sealed S3 audit batch.
     */
    public static verify_batch(batch_id: string): Promise<AxiosResponse<S3AuditBatchVerificationResult>> {
      return AuditLogService.axios.post(`${AuditLogService.base_url}/s3-audit/batches/${batch_id}:verify`)
    }

    /**
     * @description Verify a contiguous sequence of sealed S3 audit batches in one stream.
     */
    public static verify_stream_chain(
      stream_id: string,
      payload: S3AuditStreamChainVerificationRequest | null = null
    ): Promise<AxiosResponse<S3AuditStreamChainVerificationResult>> {
      return AuditLogService.axios.post(`${AuditLogService.base_url}/s3-audit/streams/${stream_id}:verify-chain`, payload)
    }

}

export default AuditLogService
